import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ConnectionStatus from './ConnectionStatus.jsx'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { t } from '../../i18n.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

// Debounce delay for real-time refetch after upstream changes (ms)
const REALTIME_DEBOUNCE = 1500

function formatPeriod(isoDate) {
  if (!isoDate) return ''
  const d = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatMoney(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return '–'
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// EVA-279: a PV factor is a ratio, not money — it shows four decimals and a
// factor of exactly 1 is a real value (month 1 in advance), so unlike
// formatMoney it must never collapse to an en dash.
function formatPvFactor(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '–'
  return n.toLocaleString('en-GB', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
}

function readDebugParam() {
  if (typeof window === 'undefined') return false
  const qs = new URLSearchParams(window.location.search)
  return qs.get('debug') === '1' || qs.get('debug') === 'true'
}

export default function CashFlowTable({ valuationId, cashFlowUrl, groupedTenants = false, translations }) {
  const hotRef = useRef(null)
  const fetchCashFlowRef = useRef(null)
  const [view, setView] = useState('monthly') // monthly | quarterly | annual
  const [detailed, setDetailed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('connecting')
  const [periodEnds, setPeriodEnds] = useState([])
  const [guaranteedIncome, setGuaranteedIncome] = useState([])
  const [tenantRows, setTenantRows] = useState([])
  const [speculativeIncome, setSpeculativeIncome] = useState([])
  const [speculativeTenantRows, setSpeculativeTenantRows] = useState([])
  const [mlaIncome, setMlaIncome] = useState([])
  const [mlaTenantRows, setMlaTenantRows] = useState([])
  const [voidDeduction, setVoidDeduction] = useState([])
  const [voidDeductionTenantRows, setVoidDeductionTenantRows] = useState([])
  const [rentFreeDeduction, setRentFreeDeduction] = useState([])
  const [rentFreeDeductionTenantRows, setRentFreeDeductionTenantRows] = useState([])
  const [rentReviewAdjustment, setRentReviewAdjustment] = useState([])
  const [rentReviewAdjustmentTenantRows, setRentReviewAdjustmentTenantRows] = useState([])
  const [currentRentFreeDeduction, setCurrentRentFreeDeduction] = useState([])
  const [currentRentFreeDeductionTenantRows, setCurrentRentFreeDeductionTenantRows] = useState([])
  const [indexation, setIndexation] = useState([])
  const [indexationTenantRows, setIndexationTenantRows] = useState([])
  const [rentFreeIndexationAdjustment, setRentFreeIndexationAdjustment] = useState([])
  const [rentFreeIndexationAdjustmentTenantRows, setRentFreeIndexationAdjustmentTenantRows] = useState([])
  const [headlineRent, setHeadlineRent] = useState([])
  const [passingRent, setPassingRent] = useState([])
  const [totalRevenue, setTotalRevenue] = useState([])
  const [creditLoss, setCreditLoss] = useState([])
  const [creditLossTenantRows, setCreditLossTenantRows] = useState([])
  // OpEx block (appended below Total Rental Revenue, EVA-273): recoveries
  // add-back → Potential Gross Revenue → Credit Loss (+ per-tenant drill-down)
  // → Effective Gross Revenue → per-line Operating Expense items → Total
  // Operating Expenses → Net Operating Income.
  const [expenseRecoveries, setExpenseRecoveries] = useState([])
  const [potentialGrossRevenue, setPotentialGrossRevenue] = useState([])
  const [effectiveGrossRevenue, setEffectiveGrossRevenue] = useState([])
  const [operatingExpenses, setOperatingExpenses] = useState([])
  const [operatingExpensesItems, setOperatingExpensesItems] = useState([])
  const [netOperatingIncome, setNetOperatingIncome] = useState([])
  // Leasing Costs block (below Net Operating Income): Tenant Improvements +
  // Leasing Commissions (each with per-tenant drill-down) and a Total TI and LC
  // subtotal. Signed negative capital deductions (TI & LC dev note).
  const [tenantImprovements, setTenantImprovements] = useState([])
  const [tenantImprovementsTenantRows, setTenantImprovementsTenantRows] = useState([])
  const [leasingCommissions, setLeasingCommissions] = useState([])
  const [leasingCommissionsTenantRows, setLeasingCommissionsTenantRows] = useState([])
  const [totalTiAndLc, setTotalTiAndLc] = useState([])
  // Capital Expenditure block (below Leasing Costs): per-line items and Total
  // Capital Expenditure. Net Cash Flow (= NOI + Total TI and LC + Total CapEx)
  // is appended last, whenever leasing costs or capex are present.
  const [capitalExpenditure, setCapitalExpenditure] = useState([])
  const [capitalExpenditureItems, setCapitalExpenditureItems] = useState([])
  const [netCashFlow, setNetCashFlow] = useState([])
  // EVA-279 discounting. Null (not []) when the valuation has no discount rate,
  // so "not discounted" stays distinguishable from "discounted to zero".
  // pv_factor is additionally null outside the monthly view: no single factor
  // describes a quarter or a year whose months each carry their own.
  const [pvFactor, setPvFactor] = useState(null)
  const [pvNetCashFlow, setPvNetCashFlow] = useState(null)
  const [debugBuckets, setDebugBuckets] = useState(null)
  const [connectionMountEl, setConnectionMountEl] = useState(null)
  const debugMode = useMemo(readDebugParam, [])

  // Mount connection badge into the server-rendered card header
  useEffect(() => {
    const container = document.querySelector('[data-react-component-component-value="CashFlowTable"]')
    const cardHeader = container?.closest('.card')?.querySelector('.card-header')
    const titleEl = cardHeader?.querySelector('h5.card-title')
    if (!titleEl) return

    const mount = document.createElement('span')
    mount.className = 'tenants-connection-status-mount'
    titleEl.appendChild(mount)
    setConnectionMountEl(mount)

    return () => { setConnectionMountEl(null); mount.remove() }
  }, [])

  // Reusable fetch — `silent` skips loading spinner (for real-time updates)
  const fetchCashFlow = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      const url = new URL(cashFlowUrl, window.location.origin)
      url.searchParams.set('view', view)
      url.searchParams.set('detailed', detailed ? 'true' : 'false')
      if (debugMode) url.searchParams.set('debug', 'true')

      const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) {
        throw new Error(json.error || `Request failed (${res.status})`)
      }

      const data = json.data || {}
      const rows = data.rows || {}
      setPeriodEnds(data.period_ends || [])
      setGuaranteedIncome(rows.guaranteed_income || [])
      setTenantRows(rows.tenants || [])
      setSpeculativeIncome(rows.speculative_income || [])
      setSpeculativeTenantRows(rows.speculative_tenants || [])
      setMlaIncome(rows.mla_income || [])
      setMlaTenantRows(rows.mla_income_tenants || [])
      setVoidDeduction(rows.void_deduction || [])
      setVoidDeductionTenantRows(rows.void_deduction_tenants || [])
      setRentFreeDeduction(rows.rent_free_deduction || [])
      setRentFreeDeductionTenantRows(rows.rent_free_deduction_tenants || [])
      setRentReviewAdjustment(rows.rent_review_adjustment || [])
      setRentReviewAdjustmentTenantRows(rows.rent_review_adjustment_tenants || [])
      setCurrentRentFreeDeduction(rows.current_rent_free_deduction || [])
      setCurrentRentFreeDeductionTenantRows(rows.current_rent_free_deduction_tenants || [])
      setIndexation(rows.indexation || [])
      setIndexationTenantRows(rows.indexation_tenants || [])
      setRentFreeIndexationAdjustment(rows.rent_free_indexation_adjustment || [])
      setRentFreeIndexationAdjustmentTenantRows(rows.rent_free_indexation_adjustment_tenants || [])
      setHeadlineRent(rows.headline_rent || [])
      setPassingRent(rows.passing_rent || [])
      setTotalRevenue(rows.total_revenue || [])
      setCreditLoss(rows.credit_loss || [])
      setCreditLossTenantRows(rows.credit_loss_tenants || [])
      setExpenseRecoveries(rows.expense_recoveries || [])
      setPotentialGrossRevenue(rows.potential_gross_revenue || [])
      setEffectiveGrossRevenue(rows.effective_gross_revenue || [])
      setOperatingExpenses(rows.operating_expenses || [])
      setOperatingExpensesItems(rows.operating_expenses_items || [])
      setNetOperatingIncome(rows.net_operating_income || [])
      setTenantImprovements(rows.tenant_improvements || [])
      setTenantImprovementsTenantRows(rows.tenant_improvements_tenants || [])
      setLeasingCommissions(rows.leasing_commissions || [])
      setLeasingCommissionsTenantRows(rows.leasing_commissions_tenants || [])
      setTotalTiAndLc(rows.total_ti_and_lc || [])
      setCapitalExpenditure(rows.capital_expenditure || [])
      setCapitalExpenditureItems(rows.capital_expenditure_items || [])
      setNetCashFlow(rows.net_cash_flow || [])
      // Kept nullable on purpose — see the state declarations above.
      setPvFactor(rows.pv_factor ?? null)
      setPvNetCashFlow(rows.pv_net_cash_flow ?? null)
      setDebugBuckets(data.debug_buckets || null)
      setError(null)
    } catch (e) {
      if (!silent) setError(e?.message || t('error_fallback', translations))
    } finally {
      setLoading(false)
    }
  }, [cashFlowUrl, view, detailed, debugMode])

  // Keep ref in sync so ActionCable callback always uses latest view/detailed
  useEffect(() => {
    fetchCashFlowRef.current = fetchCashFlow
  }, [fetchCashFlow])

  // Load on mount + reload when view/detailed change (with spinner)
  useEffect(() => {
    fetchCashFlow(false)
  }, [fetchCashFlow])

  // Subscribe to ValuationChannel — fires on Tenant / MLA / Growth changes,
  // shared with Leasing Events / Market Rent Growth tabs.
  useEffect(() => {
    if (!valuationId) return undefined
    let debounceTimer = null

    const subscription = cable.subscriptions.create(
      { channel: 'ValuationChannel', valuation_id: valuationId },
      {
        connected() { setConnectionStatus('connected') },
        disconnected() { setConnectionStatus('disconnected') },
        received() {
          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => {
            fetchCashFlowRef.current?.(true) // silent refetch
          }, REALTIME_DEBOUNCE)
        }
      }
    )

    return () => {
      subscription.unsubscribe()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [valuationId])

  const headerRows = useMemo(() => {
    const count = periodEnds.length
    if (count === 0) return []

    const periodRow = [t('headers.period_end', translations), ...periodEnds.map(formatPeriod)]
    const yearLabel = (n) => t('headers.year_n', translations, { n })
    const quarterLabel = (n) => t('headers.quarter_n', translations, { n })
    const monthLabel = (n) => t('headers.month_n', translations, { n })

    if (view === 'monthly') {
      const yearRow = [t('headers.year', translations), ...Array.from({ length: count }, (_, i) => yearLabel(Math.floor(i / 12) + 1))]
      const quarterRow = [t('headers.quarter', translations), ...Array.from({ length: count }, (_, i) => quarterLabel(Math.floor(i / 3) + 1))]
      const monthRow = [t('headers.month', translations), ...Array.from({ length: count }, (_, i) => monthLabel(i + 1))]
      return [yearRow, quarterRow, monthRow, periodRow]
    }

    if (view === 'quarterly') {
      const yearRow = [t('headers.year', translations), ...Array.from({ length: count }, (_, i) => yearLabel(Math.floor((i * 3) / 12) + 1))]
      const quarterRow = [t('headers.quarter', translations), ...Array.from({ length: count }, (_, i) => quarterLabel(i + 1))]
      return [yearRow, quarterRow, periodRow]
    }

    // annual
    const yearRow = [t('headers.year', translations), ...Array.from({ length: count }, (_, i) => yearLabel(i + 1))]
    return [yearRow, periodRow]
  }, [periodEnds, view, translations])

  // Swarm2 D-M3: split the previous 23-dep tableData memo into one section per
  // row. Each section recomputes only when its own input arrays change, and
  // the assembler memo just orders pre-built arrays.
  const buildParentRow = (label, values) => [label, ...values.map(formatMoney)]
  const buildTenantRows = (rows) => rows.map((t) => [t.title || t.id, ...(t.values || []).map(formatMoney)])

  const rowLabel = (key) => t(`rows.${key}`, translations)

  const guaranteedSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('guaranteed_income'), guaranteedIncome), tenants: buildTenantRows(tenantRows) }),
    [guaranteedIncome, tenantRows, translations]
  )
  const speculativeSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('speculative_income'), speculativeIncome), tenants: buildTenantRows(speculativeTenantRows) }),
    [speculativeIncome, speculativeTenantRows, translations]
  )
  const mlaSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('mla_income'), mlaIncome), tenants: buildTenantRows(mlaTenantRows) }),
    [mlaIncome, mlaTenantRows, translations]
  )
  const voidSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('void_deduction'), voidDeduction), tenants: buildTenantRows(voidDeductionTenantRows) }),
    [voidDeduction, voidDeductionTenantRows, translations]
  )
  const rentFreeSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('rent_free_deduction'), rentFreeDeduction), tenants: buildTenantRows(rentFreeDeductionTenantRows) }),
    [rentFreeDeduction, rentFreeDeductionTenantRows, translations]
  )
  const rentReviewSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('rent_review_adjustment'), rentReviewAdjustment), tenants: buildTenantRows(rentReviewAdjustmentTenantRows) }),
    [rentReviewAdjustment, rentReviewAdjustmentTenantRows, translations]
  )
  const currentRfSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('current_rent_free_deduction'), currentRentFreeDeduction), tenants: buildTenantRows(currentRentFreeDeductionTenantRows) }),
    [currentRentFreeDeduction, currentRentFreeDeductionTenantRows, translations]
  )
  const indexationSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('indexation'), indexation), tenants: buildTenantRows(indexationTenantRows) }),
    [indexation, indexationTenantRows, translations]
  )
  const rfIndexationSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('rent_free_indexation_adjustment'), rentFreeIndexationAdjustment), tenants: buildTenantRows(rentFreeIndexationAdjustmentTenantRows) }),
    [rentFreeIndexationAdjustment, rentFreeIndexationAdjustmentTenantRows, translations]
  )
  const creditLossSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('credit_loss'), creditLoss), tenants: buildTenantRows(creditLossTenantRows) }),
    [creditLoss, creditLossTenantRows, translations]
  )

  const headlineParentRow = useMemo(() => buildParentRow(rowLabel('headline_rent'), headlineRent), [headlineRent, translations])
  const passingParentRow = useMemo(() => buildParentRow(rowLabel('passing_rent'), passingRent), [passingRent, translations])
  const totalParentRow = useMemo(() => buildParentRow(rowLabel('total_revenue'), totalRevenue), [totalRevenue, translations])

  // OpEx block rows (appended below Total Rental Revenue). Credit Loss is
  // interleaved between Potential and Effective Gross Revenue (EVA-273), so the
  // block is split around it.
  const expenseRecoveriesRow = useMemo(() => buildParentRow(rowLabel('expense_recoveries'), expenseRecoveries), [expenseRecoveries, translations])
  const potentialGrossRow = useMemo(() => buildParentRow(rowLabel('potential_gross_revenue'), potentialGrossRevenue), [potentialGrossRevenue, translations])
  const effectiveGrossRow = useMemo(() => buildParentRow(rowLabel('effective_gross_revenue'), effectiveGrossRevenue), [effectiveGrossRevenue, translations])
  const operatingExpensesRow = useMemo(() => buildParentRow(rowLabel('operating_expenses'), operatingExpenses), [operatingExpenses, translations])
  const netOperatingIncomeRow = useMemo(() => buildParentRow(rowLabel('net_operating_income'), netOperatingIncome), [netOperatingIncome, translations])
  const opexItemRows = useMemo(
    () => operatingExpensesItems.map((item) => [item.label || item.id, ...(item.values || []).map(formatMoney)]),
    [operatingExpensesItems]
  )
  const hasOpexBlock = operatingExpenses.length > 0
  // Total Expense Recoveries → Potential Gross Revenue. Shown once the cash flow
  // has period data; Credit Loss follows immediately after.
  const recoveriesBlock = useMemo(
    () => (!hasOpexBlock ? [] : [ expenseRecoveriesRow, potentialGrossRow ]),
    [hasOpexBlock, expenseRecoveriesRow, potentialGrossRow]
  )
  // Effective Gross Revenue → [opex line items] → Total Operating Expenses →
  // Net Operating Income.
  const opexBlock = useMemo(
    () => (!hasOpexBlock ? [] : [
      effectiveGrossRow, ...opexItemRows, operatingExpensesRow, netOperatingIncomeRow
    ]),
    [hasOpexBlock, effectiveGrossRow, opexItemRows, operatingExpensesRow, netOperatingIncomeRow]
  )

  // Leasing Costs block (below NOI): Tenant Improvements (+tenant drill-down) →
  // Leasing Commissions (+tenant drill-down) → Total TI and LC subtotal. Shown
  // only when some MLA actually generates TI/LC (TI & LC dev note).
  const tenantImprovementsSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('tenant_improvements'), tenantImprovements), tenants: buildTenantRows(tenantImprovementsTenantRows) }),
    [tenantImprovements, tenantImprovementsTenantRows, translations]
  )
  const leasingCommissionsSection = useMemo(
    () => ({ parent: buildParentRow(rowLabel('leasing_commissions'), leasingCommissions), tenants: buildTenantRows(leasingCommissionsTenantRows) }),
    [leasingCommissions, leasingCommissionsTenantRows, translations]
  )
  const totalTiAndLcRow = useMemo(() => buildParentRow(rowLabel('total_ti_and_lc'), totalTiAndLc), [totalTiAndLc, translations])
  const hasLeasingCosts = useMemo(
    () => tenantImprovements.some((v) => Number(v) !== 0) || leasingCommissions.some((v) => Number(v) !== 0),
    [tenantImprovements, leasingCommissions]
  )
  const leasingCostsBlock = useMemo(
    () => (!hasLeasingCosts ? [] : [
      tenantImprovementsSection.parent, ...(detailed ? tenantImprovementsSection.tenants : []),
      leasingCommissionsSection.parent, ...(detailed ? leasingCommissionsSection.tenants : []),
      totalTiAndLcRow
    ]),
    [hasLeasingCosts, detailed, tenantImprovementsSection, leasingCommissionsSection, totalTiAndLcRow]
  )

  // CapEx block (below Leasing Costs): per-line items → Total Capital Expenditure.
  // Shown only when resolved CapEx rows exist (capex-developer-note §19).
  const capitalExpenditureRow = useMemo(() => buildParentRow(rowLabel('capital_expenditure'), capitalExpenditure), [capitalExpenditure, translations])
  const netCashFlowRow = useMemo(() => buildParentRow(rowLabel('net_cash_flow'), netCashFlow), [netCashFlow, translations])
  const capexItemRows = useMemo(
    () => capitalExpenditureItems.map((item) => [item.label || item.id, ...(item.values || []).map(formatMoney)]),
    [capitalExpenditureItems]
  )
  const capexBlock = useMemo(
    () => (capitalExpenditureItems.length === 0 ? [] : [
      ...capexItemRows, capitalExpenditureRow
    ]),
    [capitalExpenditureItems.length, capexItemRows, capitalExpenditureRow]
  )

  // EVA-279: PV rows sit immediately under Net Cash Flow, which is what they
  // discount. PV Factor comes first and only in the monthly view.
  const hasDiscounting = Array.isArray(pvNetCashFlow)
  const pvFactorRow = useMemo(
    () => (Array.isArray(pvFactor) ? [rowLabel('pv_factor'), ...pvFactor.map(formatPvFactor)] : null),
    [pvFactor, translations]
  )
  const pvNetCashFlowRow = useMemo(
    () => (hasDiscounting ? buildParentRow(rowLabel('pv_net_cash_flow'), pvNetCashFlow) : null),
    [hasDiscounting, pvNetCashFlow, translations]
  )
  const pvBlock = useMemo(
    () => (hasDiscounting ? [pvFactorRow, pvNetCashFlowRow].filter(Boolean) : []),
    [hasDiscounting, pvFactorRow, pvNetCashFlowRow]
  )

  // Net Cash Flow is the bottom total, shown whenever leasing costs or capex add
  // a line below NOI. With neither, Net Operating Income is the bottom line —
  // unless discounting is on, in which case the PV rows below need their base
  // on screen (EVA-279).
  const hasCapex = capitalExpenditureItems.length > 0
  const netCashFlowBlock = useMemo(
    () => ((hasLeasingCosts || hasCapex || hasDiscounting) ? [netCashFlowRow] : []),
    [hasLeasingCosts, hasCapex, hasDiscounting, netCashFlowRow]
  )

  const tableData = useMemo(() => {
    if (periodEnds.length === 0) return []

    // Order (EVA-159 hierarchy):
    //   Guaranteed Income    ┐
    //   Rent Review Adjust.  │  → Headline Rent
    //   Speculative Income   │
    //   MLA Income           ┘
    //   Void Deduction              ┐
    //   Rent Free Deduction         │  → Passing Rent
    //   Current Rent Free Deduction ┘
    //   Indexation
    //   Rent Free Indexation Adjustment (EVA-170)
    //   = Total Rental Revenue
    //   Total Expense Recoveries                        ┐
    //   = Potential Gross Revenue (EVA-273)             │  Net Rental Revenue is
    //   Credit Loss (EVA-187, now billed off PGR)       │  gone — Credit Loss now
    //   = Effective Gross Revenue (EVA-273)             ┘  sits below recoveries
    if (groupedTenants) {
      return [
        ...headerRows,
        guaranteedSection.parent, ...(detailed ? guaranteedSection.tenants : []),
        rentReviewSection.parent, ...(detailed ? rentReviewSection.tenants : []),
        speculativeSection.parent, ...(detailed ? speculativeSection.tenants : []),
        mlaSection.parent, ...(detailed ? mlaSection.tenants : []),
        headlineParentRow,
        voidSection.parent, ...(detailed ? voidSection.tenants : []),
        rentFreeSection.parent, ...(detailed ? rentFreeSection.tenants : []),
        currentRfSection.parent, ...(detailed ? currentRfSection.tenants : []),
        passingParentRow,
        indexationSection.parent, ...(detailed ? indexationSection.tenants : []),
        rfIndexationSection.parent, ...(detailed ? rfIndexationSection.tenants : []),
        totalParentRow,
        ...recoveriesBlock,
        creditLossSection.parent, ...(detailed ? creditLossSection.tenants : []),
        ...opexBlock,
        ...leasingCostsBlock,
        ...capexBlock,
        ...netCashFlowBlock,
        ...pvBlock
      ]
    }

    return [
      ...headerRows,
      guaranteedSection.parent,
      rentReviewSection.parent,
      speculativeSection.parent,
      mlaSection.parent,
      headlineParentRow,
      voidSection.parent,
      rentFreeSection.parent,
      currentRfSection.parent,
      passingParentRow,
      indexationSection.parent,
      rfIndexationSection.parent,
      totalParentRow,
      ...(detailed ? guaranteedSection.tenants : []),
      ...(detailed ? rentReviewSection.tenants : []),
      ...(detailed ? speculativeSection.tenants : []),
      ...(detailed ? mlaSection.tenants : []),
      ...(detailed ? voidSection.tenants : []),
      ...(detailed ? rentFreeSection.tenants : []),
      ...(detailed ? currentRfSection.tenants : []),
      ...(detailed ? indexationSection.tenants : []),
      ...(detailed ? rfIndexationSection.tenants : []),
      ...(detailed ? creditLossSection.tenants : []),
      ...recoveriesBlock,
      creditLossSection.parent,
      ...opexBlock,
      ...leasingCostsBlock,
      ...capexBlock,
      ...netCashFlowBlock,
      ...pvBlock
    ]
  }, [
    periodEnds.length, headerRows, detailed, groupedTenants,
    guaranteedSection, speculativeSection, mlaSection, voidSection,
    rentFreeSection, rentReviewSection, currentRfSection, indexationSection,
    rfIndexationSection, creditLossSection, headlineParentRow, passingParentRow,
    totalParentRow, recoveriesBlock, opexBlock, leasingCostsBlock, capexBlock, netCashFlowBlock,
    pvBlock
  ])

  const headerRowCount = headerRows.length

  // Compute indented (tenant), subtotal (Headline / Passing) and total row
  // positions for cell styling. Section order MUST match tableData above.
  const { indentedRows, subtotalRows, totalRevenueRowIdx } = useMemo(() => {
    const indented = new Set()
    const subtotals = new Set()
    let totalIdx = -1
    let row

    if (groupedTenants) {
      row = headerRowCount
      // null tenants list = subtotal-only row (Headline / Passing), no children.
      const sections = [
        { kind: 'parent',   tenants: detailed ? tenantRows : [] },
        { kind: 'parent',   tenants: detailed ? rentReviewAdjustmentTenantRows : [] },
        { kind: 'parent',   tenants: detailed ? speculativeTenantRows : [] },
        { kind: 'parent',   tenants: detailed ? mlaTenantRows : [] },
        { kind: 'subtotal', tenants: [] }, // Headline Rent
        { kind: 'parent',   tenants: detailed ? voidDeductionTenantRows : [] },
        { kind: 'parent',   tenants: detailed ? rentFreeDeductionTenantRows : [] },
        { kind: 'parent',   tenants: detailed ? currentRentFreeDeductionTenantRows : [] },
        { kind: 'subtotal', tenants: [] }, // Passing Rent
        { kind: 'parent',   tenants: detailed ? indexationTenantRows : [] },
        { kind: 'parent',   tenants: detailed ? rentFreeIndexationAdjustmentTenantRows : [] },
        { kind: 'subtotal', tenants: [] } // Total Rental Revenue (demoted; NOI is the total)
      ]
      for (const section of sections) {
        if (section.kind === 'subtotal') subtotals.add(row)
        row += 1 // parent / subtotal row
        for (let i = 0; i < section.tenants.length; i += 1) {
          indented.add(row)
          row += 1
        }
      }
    } else {
      // Flat layout: 11 rows before Total Rental Revenue = 4 income + Headline +
      // 3 deductions + Passing + Indexation + Rent Free Indexation Adjustment.
      // Total Rental Revenue (idx 11) is a subtotal, then every tenant group.
      subtotals.add(headerRowCount + 4).add(headerRowCount + 8).add(headerRowCount + 11)
      row = headerRowCount + 12
      if (detailed) {
        const tenantGroups = [
          tenantRows, rentReviewAdjustmentTenantRows, speculativeTenantRows,
          mlaTenantRows, voidDeductionTenantRows, rentFreeDeductionTenantRows,
          currentRentFreeDeductionTenantRows, indexationTenantRows,
          rentFreeIndexationAdjustmentTenantRows, creditLossTenantRows
        ]
        for (const group of tenantGroups) {
          for (let i = 0; i < group.length; i += 1) {
            indented.add(row)
            row += 1
          }
        }
      }
    }

    // EVA-273 tail, identical in both layouts: Total Expense Recoveries →
    // Potential Gross Revenue → Credit Loss (+ its tenants in the grouped
    // layout) → Effective Gross Revenue → opex items → Total Operating Expenses
    // → Net Operating Income (the bottom total). `row` currently points at Total
    // Expense Recoveries.
    if (operatingExpenses.length > 0) {
      row += 1 // Total Expense Recoveries
      subtotals.add(row) // Potential Gross Revenue
      row += 1
    }
    row += 1 // Credit Loss (parent, data)
    if (groupedTenants && detailed) {
      for (let i = 0; i < creditLossTenantRows.length; i += 1) {
        indented.add(row)
        row += 1
      }
    }
    if (operatingExpenses.length > 0) {
      subtotals.add(row) // Effective Gross Revenue
      row += 1
      for (let i = 0; i < operatingExpensesItems.length; i += 1) {
        indented.add(row)
        row += 1
      }
      subtotals.add(row) // Total Operating Expenses
      row += 1
      totalIdx = row // Net Operating Income = bottom total
    } else {
      totalIdx = row - 1 // no OpEx data: Credit Loss is the last row
    }

    // Leasing Costs block appended below Net Operating Income: Tenant
    // Improvements (+tenants) → Leasing Commissions (+tenants) → Total TI and LC
    // subtotal. NOI is demoted to a subtotal.
    if (hasLeasingCosts) {
      subtotals.add(totalIdx) // demote Net Operating Income
      row = totalIdx + 1
      row += 1 // Tenant Improvements (parent, data)
      if (detailed) for (let i = 0; i < tenantImprovementsTenantRows.length; i += 1) { indented.add(row); row += 1 }
      row += 1 // Leasing Commissions (parent, data)
      if (detailed) for (let i = 0; i < leasingCommissionsTenantRows.length; i += 1) { indented.add(row); row += 1 }
      subtotals.add(row) // Total TI and LC
      totalIdx = row
    }

    // CapEx block appended below Leasing Costs (items → Total Capital
    // Expenditure). The previous bottom row is demoted to a subtotal.
    if (capitalExpenditureItems.length > 0) {
      subtotals.add(totalIdx) // demote NOI or Total TI and LC
      const capexItemsStart = totalIdx + 1
      for (let i = 0; i < capitalExpenditureItems.length; i += 1) indented.add(capexItemsStart + i)
      subtotals.add(capexItemsStart + capitalExpenditureItems.length) // Total Capital Expenditure
      totalIdx = capexItemsStart + capitalExpenditureItems.length
    }

    // Net Cash Flow = bottom total, appended whenever leasing costs or capex add
    // a line below NOI, or whenever discounting needs it on screen as the base of
    // the PV rows (EVA-279). The previous bottom (NOI / Total TI and LC / Total
    // CapEx) is demoted to a subtotal. This condition MUST track netCashFlowBlock.
    if (hasLeasingCosts || capitalExpenditureItems.length > 0 || hasDiscounting) {
      subtotals.add(totalIdx)
      totalIdx += 1 // Net Cash Flow
    }

    // EVA-279: PV Factor is a supporting line (indented); PV Net Cash Flow becomes
    // the bottom total and demotes Net Cash Flow to a subtotal.
    if (hasDiscounting) {
      subtotals.add(totalIdx)
      if (pvFactorRow) {
        totalIdx += 1
        indented.add(totalIdx)
      }
      totalIdx += 1 // PV Net Cash Flow
    }

    return { indentedRows: indented, subtotalRows: subtotals, totalRevenueRowIdx: totalIdx }
  }, [groupedTenants, detailed, headerRowCount,
      tenantRows.length, rentReviewAdjustmentTenantRows.length, speculativeTenantRows.length,
      mlaTenantRows.length, voidDeductionTenantRows.length,
      rentFreeDeductionTenantRows.length, currentRentFreeDeductionTenantRows.length,
      indexationTenantRows.length, rentFreeIndexationAdjustmentTenantRows.length,
      creditLossTenantRows.length, operatingExpenses.length, operatingExpensesItems.length,
      capitalExpenditureItems.length, hasLeasingCosts,
      tenantImprovementsTenantRows.length, leasingCommissionsTenantRows.length,
      hasDiscounting, pvFactorRow])

  // Expose the Handsontable instance for end-to-end specs (mirrors __evaMlaHot).
  // The table virtualises rows, so specs read row labels from the instance
  // rather than the rendered viewport.
  useEffect(() => {
    if (typeof window !== 'undefined' && !loading) {
      window.__evaCashFlowHot = hotRef.current?.hotInstance
    }
    return () => {
      if (typeof window !== 'undefined') delete window.__evaCashFlowHot
    }
  })

  return (
    <div className="cash-flow-table-container">
      <div className="cash-flow-toolbar">
        <div className="btn-group" role="group" aria-label={t('view.aria_label', translations)}>
          <button
            type="button"
            className={`btn btn-sm ${view === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setView('monthly')}
          >
            {t('view.monthly', translations)}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${view === 'quarterly' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setView('quarterly')}
          >
            {t('view.quarterly', translations)}
          </button>
          <button
            type="button"
            className={`btn btn-sm ${view === 'annual' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setView('annual')}
          >
            {t('view.annual', translations)}
          </button>
        </div>

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setDetailed((v) => !v)}
        >
          {detailed ? t('toggle_tenants.hide', translations) : t('toggle_tenants.show', translations)}
        </button>
      </div>

      {error && <div className="alert alert-danger mb-2">{error}</div>}

      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading', translations)}</span>
          </div>
        </div>
      ) : (
        <div className="cash-flow-table-viewport">
          <HotTable
            ref={hotRef}
            data={tableData}
            rowHeaders={false}
            colHeaders={false}
            fixedColumnsStart={1}
            themeName="ht-theme-horizon"
            licenseKey={HOT_LICENSE_KEY}
            sanitizer={sanitizeHandsontableHtml}
            stretchH="all"
            width="100%"
            height="60vh"
            readOnly={true}
            manualColumnResize={true}
            manualRowResize={false}
            cells={(row, col) => {
              if (col === 0 && indentedRows.has(row)) return { readOnly: true, className: 'cash-flow-indent' }
              if (row === totalRevenueRowIdx) return { readOnly: true, className: 'cash-flow-total htRight htMiddle' }
              if (subtotalRows.has(row)) return { readOnly: true, className: 'cash-flow-subtotal htRight htMiddle' }
              if (col === 0) return { readOnly: true }
              if (row < headerRowCount) return { readOnly: true, className: 'htRight htMiddle' }
              return { readOnly: true, className: 'htRight htMiddle' }
            }}
          />
        </div>
      )}

      {debugMode && debugBuckets && <DebugDrawer buckets={debugBuckets} translations={translations} />}

      <ConnectionStatus
        status={connectionStatus}
        mountEl={connectionMountEl}
        labels={{
          connected: t('connection.live', translations),
          disconnected: t('connection.disconnected', translations),
          connecting: t('connection.connecting', translations),
        }}
      />
    </div>
  )
}

const DEBUG_COLUMN_KEYS = [
  ['tenant_label', 'tenant'],
  ['event_type', 'event'],
  ['mla_cycle', 'cycle'],
  ['payment_schedule', 'schedule'],
  ['bucket_payment_date', 'payment'],
  ['bucket_coverage_start', 'cov_start'],
  ['bucket_coverage_end', 'cov_end'],
  ['full_bucket_amount_before_proration', 'full'],
  ['proration_factor', 'factor'],
  ['final_bucket_amount', 'final'],
  ['debug_notes', 'notes']
]

function DebugDrawer({ buckets, translations }) {
  const sections = [
    [t('debug.sections.mla', translations), buckets.mla],
    [t('debug.sections.void', translations), buckets.void],
    [t('debug.sections.rent_free', translations), buckets.rent_free]
  ]
  const counts = t('debug.counts', translations, {
    mla: (buckets.mla || []).length,
    void: (buckets.void || []).length,
    rent_free: (buckets.rent_free || []).length
  })
  return (
    <details className="cash-flow-debug-drawer mt-3">
      <summary>{t('debug.title', translations)} {counts}</summary>
      {sections.map(([label, rows]) => (
        <div key={label} className="cash-flow-debug-section">
          <h6 className="mt-3">{label}</h6>
          <div className="table-responsive">
            <table className="table table-sm table-striped table-bordered">
              <thead>
                <tr>{DEBUG_COLUMN_KEYS.map(([_, colKey]) => <th key={colKey}>{t(`debug.columns.${colKey}`, translations)}</th>)}</tr>
              </thead>
              <tbody>
                {(rows || []).map((bucket) => (
                  <tr key={bucket.bucket_id}>
                    {DEBUG_COLUMN_KEYS.map(([dataKey]) => <td key={dataKey}>{String(bucket[dataKey] ?? '')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </details>
  )
}
